import { ApiResponse, OmitFirstArg } from '../types';
import ky, { KyInstance, Options } from 'ky';

// One currency's referral fee-share earnings (ADR-048 WS-B). Amounts are raw
// decimal strings at `decimals` scale — BSC USDT and AIP are BOTH 18dp on the
// ledger, so render via CurrencyView with decimal={token.decimals}, NEVER the
// global base-token lens (which would render a USDT row with AIP's icon/scale;
// same accident class as the dual-token history bug). Exactly one of `claimable`
// / `pendingWalletBind` is non-zero: claimable when a wallet is bound, else held.
export type ReferralTokenEarning = {
  currency: string;
  decimals: number;
  accrued: string;
  paid: string;
  claimable: string;
  pendingWalletBind: string;
};

export type ReferralEarnings = {
  // null when the caller has no linked wallet — their balance is held (not lost),
  // credited on bind. Never render held amounts in an error/loss color.
  boundWallet: string | null;
  // Config-gated on the server; dark until the audited Merkle distributor ships.
  claimEnabled: boolean;
  tokens: ReferralTokenEarning[];
};

/**
 * Get referral fee-share earnings for the signed-in user.
 * [need auth] — uid is resolved from the JWT server-side, never a param, so a
 * caller can only ever read their own earnings.
 * @param api - The KyInstance (referral-prefixed)
 * @param options - Additional request options
 * @returns Promise resolving to the caller's per-currency earnings
 * @example
 * const { data } = await getReferralEarnings();
 */
export const getReferralEarnings = async (
  api: KyInstance,
  options?: Options
): Promise<ApiResponse<ReferralEarnings>> => {
  return await api.get<ApiResponse<ReferralEarnings>>('my-earnings', options).json();
};

export type ReferralApi = {
  getReferralEarnings: OmitFirstArg<typeof getReferralEarnings>;
};

export const createReferralApi = (initOptions: Options): ReferralApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/referral` };
  const referralApi = ky.extend(mergedOptions);
  return {
    getReferralEarnings: options => getReferralEarnings(referralApi, options),
  };
};

export default createReferralApi;
